import {
  useForm,
  Mode,
  FieldValues,
  Control,
  PathValue,
  Path,
  UseFormReset,
  UseFormSetValue,
  useController,
} from "react-hook-form";
import { TSelectItem } from "../select/select";
import { TAutoCompleteValues, TInputModeValues } from "../../../types/form-element-types";
import { Fragment, useEffect, useState } from "react";
import { TCheckboxProps } from "../checkbox-group/checkbox-group";
import { PrimaryButton } from "../../elements";
import {
  HookFormCheckboxGroup,
  HookFormFileUpload,
  HookFormInputField,
  THookFormInputRules,
  HookFormSelect,
  HookFormTextArea,
  THookFormCheckBoxGroupRules,
  THookFormTextAreaRules,
  THookFormFileUploadRules,
  THookFormSelectRules,
} from "../hook-form";
import { isArrayWithItems } from "../../../helpers";
import AlertSummary, { TAlertSummary } from "../../elements/alert-summary/alert-summary";
import { getErrorsFromHookFormError } from "../../../helpers/form-helper";
import { useTranslation } from "react-i18next";
import { TRadioButtonGroupProps, TRadioButtonProps } from "../radio-button-group/radio-button-group";
import { HookFormRadioButtonGroup, THookFormRadioButtonGroupRules } from "../hook-form/hook-form-radio-button-group";
import AcceptTermsAndSubmit from "../components/accept-terms/accept-terms";

export type TDynamicForm = {
  mode: Mode;
  reValidateMode: Exclude<Mode, "onTouched" | "all">;
  fields: Array<TDynamicField>;
};

export type TDynamicFormField =
  | TInputField
  | TTextArea
  | TSelectField
  | TCheckboxGroup
  | TFileUpload
  | TRadioButtonGroup;

export type TDynamicField = TDynamicFormField | TContent;

const isDynamicFormField = (field: TDynamicField): field is TDynamicFormField => {
  if (field.type === "content") {
    return false;
  }
  return true;
};

export type TDynamicFormPostStatus = "notposted" | "success" | "error";

type TRenderCondition = {
  dependency: Map<string, Array<string>>;
  fieldStateUntilConditionMet: "disabled" | "hidden";
};

type TContent = {
  type: Extract<TDynamicFormInputTypes, "content">;
  name: string;
  value: React.ReactNode;
  renderCondition?: TRenderCondition;
};

type TBaseField<T> = {
  name: string;
  label: string;
  helperText?: string;
  renderCondition?: TRenderCondition;
  defaultValue?: T;
};

type TInputField = TBaseField<string> & {
  type: Extract<TDynamicFormInputTypes, "text">;
  inputMode?: TInputModeValues;
  placeholder?: string;
  isDisabled?: boolean;
  autoComplete?: TAutoCompleteValues;
  rules?: THookFormInputRules;
};

type TTextArea = TBaseField<string> & {
  type: Extract<TDynamicFormInputTypes, "textarea">;
  placeholder?: string;
  isDisabled?: boolean;
  rules?: THookFormTextAreaRules;
  maxLength?: number;
  minLength?: number;
};

type TSelectField = TBaseField<string> & {
  type: Extract<TDynamicFormInputTypes, "select">;
  placeholder?: string;
  items: Array<TSelectItem>;
  isDisabled?: boolean;
  rules?: THookFormSelectRules;
};

type TCheckboxGroup = TBaseField<Array<string>> & {
  type: Extract<TDynamicFormInputTypes, "checkbox">;
  labelContent?: React.ReactNode;
  isDisabled?: boolean;
  checkboxes: Array<TCheckboxProps>;
  rules?: THookFormCheckBoxGroupRules;
};

type TRadioButtonGroup = TBaseField<string> & {
  type: Extract<TDynamicFormInputTypes, "radiobutton">;
  labelContent?: React.ReactNode;
  isDisabled?: boolean;
  radioButtons: Array<TRadioButtonProps>;
  rules?: THookFormRadioButtonGroupRules;
  orientation?: TRadioButtonGroupProps["orientation"];
};

type TFileUpload = TBaseField<File | Array<File>> & {
  type: Extract<TDynamicFormInputTypes, "fileupload">;
  isDisabled?: boolean;
  accept?: string;
  allowMultipleFiles?: boolean;
  rules?: THookFormFileUploadRules;
};

type TDynamicFormInputTypes =
  | "text"
  | "date"
  | "number"
  | "fileupload"
  | "textarea"
  | "checkbox"
  | "radiobutton"
  | "select"
  | "content";

type TFormUtilities = {
  reset: UseFormReset<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
};

export type OnFormSubmitFunc = (fieldValues: FieldValues, formUtilities: TFormUtilities) => void;
export type OnFormChangeFunc = (fieldValues: FieldValues, formUtilities: TFormUtilities) => void;

type TAcceptTerms = {
  title: string;
  termsContent: React.ReactNode;
  acceptTermsLabel: string;
};

type TDynamicFormProps<T extends TDynamicForm> = {
  form: T;
  initialFormValues?: FieldValues;
  isDisabled?: boolean;
  onFormSubmit?: OnFormSubmitFunc;
  onFormChange?: OnFormChangeFunc;
  isPostingForm?: boolean;
  formStatus?: TAlertSummary;
  labels?: TDynamicFormLabels;
  hideSubmitButton?: boolean;
  acceptTerms?: TAcceptTerms;
};

type TDynamicFormLabels = {
  submitButtonText?: string;
  submitButtonLoadingText?: string;
};

const shouldRenderField = (field: TDynamicField, formData: FieldValues | null) => {
  if (field.renderCondition && field.renderCondition.dependency.size > 0) {
    let shouldRender = false;
    for (const [key, value] of field.renderCondition.dependency.entries()) {
      if (formData && formData[key]) {
        shouldRender = value.includes(formData[key]);
      }

      if (shouldRender) {
        return true;
      }
    }
    return shouldRender;
  }
  return true;
};

const createDefaultValueObjectFromFields = (fields: Array<TDynamicField>): FieldValues => {
  const onlyFormFields = fields.filter((field) => isDynamicFormField(field));
  const defaultValues = onlyFormFields.reduce<FieldValues>((values, field) => {
    if (field.type === "select" && !field.defaultValue && isArrayWithItems(field.items)) {
      values[field.name] = field.items[0].value;
    } else if (field.type === "checkbox" && !field.defaultValue) {
      values[field.name] = []; // Initiate with empty array
    } else if (field.defaultValue) {
      values[field.name] = field.defaultValue;
    }
    return values;
  }, {});
  return defaultValues;
};

const SubmitForm = ({
  acceptTerms,
  labels,
  isPostingForm,
  onSubmitForm,
  control,
}: {
  acceptTerms?: TAcceptTerms;
  labels?: TDynamicFormLabels;
  isPostingForm?: boolean;
  onSubmitForm: () => void;
  control: Control<FieldValues, unknown>;
}) => {
  const { t } = useTranslation();
  if (acceptTerms) {
    const { field } = useController({
      name: "acceptTerms",
      control: control,
      rules: { required: true },
      shouldUnregister: true,
    });
    return (
      <AcceptTermsAndSubmit
        title={acceptTerms.title}
        isPostingForm={isPostingForm}
        onSubmitForm={onSubmitForm}
        termsContent={acceptTerms.termsContent}
        submitButtonText={labels?.submitButtonText ?? t("SEND")}
        submitButtonLoadingText={labels?.submitButtonLoadingText ?? t("SENDING")}
        checkboxProps={{
          name: field.name,
          value: field.value,
          onChange: field.onChange,
          label: acceptTerms.acceptTermsLabel,
          isChecked: field.value === true,
        }}
      />
    );
  } else {
    return (
      <PrimaryButton
        isFullWidth={true}
        label={
          isPostingForm ? (labels?.submitButtonLoadingText ?? t("SENDING")) : (labels?.submitButtonText ?? t("SEND"))
        }
        isLoading={isPostingForm}
        onClick={onSubmitForm}
      />
    );
  }
};

const DynamicForm = <T extends TDynamicForm>({
  form,
  initialFormValues,
  onFormSubmit,
  onFormChange,
  isDisabled,
  isPostingForm,
  formStatus,
  labels,
  hideSubmitButton,
  acceptTerms,
}: TDynamicFormProps<T>) => {
  const { t } = useTranslation();
  const defaultValues = initialFormValues ? initialFormValues : createDefaultValueObjectFromFields(form.fields);
  const { control, watch, handleSubmit, formState, reset, setValue } = useForm({
    mode: form.mode,
    reValidateMode: form.reValidateMode,
  });

  const formUtilities = { reset, setValue } satisfies TFormUtilities;

  const { errors } = formState;
  const fieldNameAndLabel = form.fields
    .filter((field) => isDynamicFormField(field))
    .reduce<Record<string, string>>((fieldNameAndLabel, field) => {
      fieldNameAndLabel[field.name] = field.label;
      return fieldNameAndLabel;
    }, {});

  if (acceptTerms) {
    fieldNameAndLabel["acceptTerms"] = acceptTerms.acceptTermsLabel ?? t("ACCEPT_TERMS");
  }

  const formErrors = getErrorsFromHookFormError(errors, fieldNameAndLabel, t);

  const [formData, setFormData] = useState<FieldValues | null>(defaultValues);
  useEffect(() => {
    const { unsubscribe } = watch((formValues) => {
      setFormData(formValues);
      if (typeof onFormChange === "function") {
        onFormChange(formValues, formUtilities);
      }
    });
    return () => unsubscribe();
  }, [watch]);

  const onValidFormSubmit = (fieldValues: FieldValues) => {
    if (typeof onFormSubmit === "function") {
      onFormSubmit(fieldValues, formUtilities);
    }
  };

  return (
    <div>
      {form.fields.map((field) => {
        const shouldRender = field.renderCondition ? shouldRenderField(field, formData) : true;
        const isDisabledUntilConditionMet =
          !shouldRender && field.renderCondition?.fieldStateUntilConditionMet === "disabled";
        if (shouldRender || isDisabledUntilConditionMet) {
          const defaultValueForField = defaultValues[field.name] ?? undefined;
          return (
            <DynamicElementRenderer
              key={field.name}
              field={field}
              control={control}
              isDisabled={isDisabledUntilConditionMet || isDisabled || isPostingForm}
              defaultValue={defaultValueForField}
            />
          );
        }
        return undefined;
      })}
      {isArrayWithItems(formErrors) && (
        <AlertSummary className={"mb-md"} heading={t("ERROR_MISSING_INFORMATION_HEADING")} messages={formErrors} />
      )}
      {formStatus && <AlertSummary className={"mb-md"} {...formStatus} />}
      {!hideSubmitButton && (
        <SubmitForm
          onSubmitForm={handleSubmit(onValidFormSubmit)}
          isPostingForm={isPostingForm}
          labels={labels}
          acceptTerms={acceptTerms}
          control={control}
        />
      )}
    </div>
  );
};

export default DynamicForm;

type TDynamicElementRendererProps<T extends FieldValues> = {
  field: TDynamicField;
  control: Control<FieldValues, unknown>;
  isDisabled?: boolean;
  defaultValue?: PathValue<T, Path<T>>;
};

/**
 * Render form element from props
 */
const DynamicElementRenderer = ({
  field,
  control,
  isDisabled,
  defaultValue,
}: TDynamicElementRendererProps<FieldValues>) => {
  switch (field.type) {
    case "text": {
      return (
        <HookFormInputField
          control={control}
          name={field.name}
          rules={field.rules}
          inputMode={field.inputMode ?? "text"}
          label={field.label}
          helperText={field.helperText}
          placeholder={field.placeholder}
          autoComplete={field.autoComplete}
          isDisabled={field.isDisabled || isDisabled}
          defaultValue={defaultValue}
        />
      );
    }
    case "textarea": {
      return (
        <HookFormTextArea
          control={control}
          name={field.name}
          rules={field.rules}
          label={field.label}
          helperText={field.helperText}
          placeholder={field.placeholder}
          isDisabled={field.isDisabled || isDisabled}
          maxLength={field.maxLength}
          minLength={field.minLength}
          defaultValue={defaultValue}
        />
      );
    }
    case "select": {
      return (
        <HookFormSelect
          control={control}
          name={field.name}
          rules={field.rules}
          helperText={field.helperText}
          label={field.label}
          items={field.items}
          isDisabled={field.isDisabled || isDisabled}
          defaultValue={defaultValue}
          placeholder={field.placeholder}
        />
      );
    }
    case "checkbox": {
      return (
        <HookFormCheckboxGroup
          control={control}
          name={field.name}
          rules={field.rules}
          label={field.labelContent ? field.labelContent : field.label}
          helperText={field.helperText}
          isDisabled={field.isDisabled || isDisabled}
          checkboxes={field.checkboxes}
          defaultValue={defaultValue}
        />
      );
    }
    case "radiobutton": {
      return (
        <HookFormRadioButtonGroup
          control={control}
          name={field.name}
          rules={field.rules}
          label={field.labelContent ? field.labelContent : field.label}
          helperText={field.helperText}
          isDisabled={field.isDisabled || isDisabled}
          defaultValue={defaultValue}
          radioButtons={field.radioButtons}
          orientation={field.orientation}
        />
      );
    }
    case "fileupload": {
      return (
        <HookFormFileUpload
          control={control}
          name={field.name}
          rules={field.rules}
          label={field.label}
          helperText={field.helperText}
          isDisabled={field.isDisabled || isDisabled}
          accept={field.accept}
          allowMultipleFiles={field.allowMultipleFiles}
          defaultValue={defaultValue}
        />
      );
    }
    case "content": {
      return <Fragment key={field.name}>{field.value}</Fragment>;
    }
  }
};
